"use client"

import * as React from "react"

import { cn } from "../../lib/utils"

/**
 * v5.2 Checkbox — native `<input type="checkbox">` visually re-drawn
 * via `.ac-check` CSS. The native input is real (sr-only); keyboard
 * and screen-reader behaviour is preserved.
 *
 * Usage:
 *   <Checkbox checked onCheckedChange={setChecked}>Recurring campaign</Checkbox>
 *
 * Works as uncontrolled (`defaultChecked`) or controlled (`checked`).
 * Pass `indeterminate` for the mixed state.
 */
export interface CheckboxProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "type"> {
  /** Called with the new checked state on change. */
  onCheckedChange?: (checked: boolean) => void
  /** Visual mixed state. */
  indeterminate?: boolean
  /** Label rendered to the right of the mark. */
  children?: React.ReactNode
}

const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  (
    { className, indeterminate, disabled, onCheckedChange, onChange, children, ...props },
    forwardedRef
  ) => {
    const innerRef = React.useRef<HTMLInputElement>(null)
    React.useImperativeHandle(forwardedRef, () => innerRef.current as HTMLInputElement)

    React.useEffect(() => {
      if (innerRef.current) innerRef.current.indeterminate = Boolean(indeterminate)
    }, [indeterminate])

    return (
      <label className={cn("ac-check", disabled && "is-disabled", className)}>
        <input
          ref={innerRef}
          type="checkbox"
          disabled={disabled}
          onChange={(e) => {
            onCheckedChange?.(e.target.checked)
            onChange?.(e)
          }}
          {...props}
        />
        <span className="mark" />
        {children != null && <span className="label-text">{children}</span>}
      </label>
    )
  }
)
Checkbox.displayName = "Checkbox"

export { Checkbox }
